import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEpisodes } from '../../hooks/useEpisodes';
import { useAuth } from '../../hooks/useAuth';
import { useIndustries } from '../../hooks/useIndustries';
import RegistrationModal from '../../components/auth/RegistrationModal';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';

export default function Home() {
  const { user, loginWithSubscriberData } = useAuth();
  const { industries } = useIndustries();
  const navigate = useNavigate();

  // Use the episodes hook to get latest episodes with error handling
  const { getLatestEpisodes, loading: episodesLoading, error: episodesError } = useEpisodes();
  const latestEpisodes = getLatestEpisodes(3);

  // Registration form state
  const [step, setStep] = useState<'form' | 'verification'>('form');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    industry: '',
    agreeToTerms: false
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [devCode, setDevCode] = useState<string>('');

  // Modal state for bottom subscribe section
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands',
    'Sweden', 'Norway', 'Denmark', 'Finland', 'Australia', 'New Zealand', 'Japan', 'South Korea',
    'Singapore', 'Hong Kong', 'Switzerland', 'Austria', 'Belgium', 'Ireland', 'Portugal', 'Other'
  ];

  const handleRegistrationSuccess = (newUser: any) => {
    console.log('Registration successful:', newUser);
    setIsRegistrationModalOpen(false);
    // Use the correct login function from useAuth
    if (loginWithSubscriberData && newUser) {
      loginWithSubscriberData(newUser);
    }
    // Redirect to episodes page after successful registration
    navigate('/episodes');
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.country) return;

    setIsLoading(true);
    setError('');
    setDevCode('');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Supabase configuration is missing. Please check environment variables.');
      }
      
      const requestBody = {
        email: formData.email.trim(),
        type: 'registration',
        name: formData.name.trim(),
        country: formData.country,
        industry: formData.industry
      };
      
      const response = await fetch(`${supabaseUrl}/functions/v1/send-verification-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify(requestBody),
      });

      let data;
      try {
        const responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }

      setExpiresAt(data.expires_at);
      
      if (data.dev_mode && data.test_code) {
        setDevCode(data.test_code);
        setSuccessMessage(`Development Mode: Your verification code is ${data.test_code}. Also check your email and spam folder.`);
      } else {
        setSuccessMessage('Verification code sent to your email! Please check your inbox and spam folder.');
      }
      
      setStep('verification');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode.trim() || verificationCode.length !== 6) return;

    setIsLoading(true);
    setError('');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      const response = await fetch(`${supabaseUrl}/functions/v1/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          code: verificationCode.trim(),
          type: 'registration',
          name: formData.name.trim(),
          country: formData.country,
          industry: formData.industry
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code');
      }

      // Refresh the user state
      if (loginWithSubscriberData && data.subscriber) {
        loginWithSubscriberData(data.subscriber);
      }
      
      // Reset form
      setStep('form');
      setFormData({ name: '', email: '', country: '', industry: '', agreeToTerms: false });
      setVerificationCode('');
      setError('');
      setSuccessMessage('');
      setExpiresAt('');
      setDevCode('');
      
      // Redirect to episodes page after successful registration
      navigate('/episodes');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify code');
    } finally {
      setIsLoading(false);
    }
  };

  const formatExpiry = (expiryString: string) => {
    if (!expiryString) return '';
    const expiry = new Date(expiryString);
    return expiry.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // SEO Schema.org data for homepage
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "DBD Podcast",
    "alternateName": "Distilled Business Dialogue",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
    "description": "Where business wisdom meets the art of distillation. Deep conversations about entrepreneurship, leadership, and the psychology behind successful ventures.",
    "publisher": {
      "@type": "Organization",
      "name": "DBD Podcast",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/episodes?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "DBD Podcast",
    "alternateName": "Distilled Business Dialogue",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
    "logo": "https://static.readdy.ai/image/ad65d83c1087407616f0591309583e77/5910930edf8f8435ab6c0a56926fd219.png",
    "description": "Premium business podcast featuring deep conversations about entrepreneurship, leadership, and industry expertise.",
    "sameAs": [
      "https://aliassmith.com",
      "https://jumpify.net",
      "https://loschapulines.org"
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title="DBD Podcast - Distilled Business Dialogue | Premium Business Insights"
        description="Where business wisdom meets the art of distillation. Deep conversations about entrepreneurship, leadership, the psychology behind successful ventures. Subscribe for exclusive early access to episodes."
        keywords="business podcast, entrepreneurship, leadership, business insights, distilled business dialogue, premium podcast, business psychology, industry expertise"
        ogTitle="DBD Podcast - Distilled Business Dialogue"
        ogDescription="Where business wisdom meets the art of distillation. Deep conversations about entrepreneurship, leadership, the psychology behind successful ventures."
        ogImage="https://static.readdy.ai/image/ad65d83c1087407616f0591309583e77/5910930edf8f8435ab6c0a56926fd219.png"
        ogType="website"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`}
      />
      
      <SchemaOrg data={websiteSchema} />
      <SchemaOrg data={organizationSchema} />
      <SchemaOrg data={breadcrumbSchema} />

      {/* Hero Section */}
      <section 
        className="relative min-h-screen bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/dba7c368279e729caffac595bf25b065.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent"></div>
        
        <div className="relative z-10 container mx-auto px-6 py-20 flex items-center min-h-screen">
          <div className="w-full max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left Content */}
              <div>
                <div className="mb-8">
                  <div className="mb-6">
                    <img 
                      src="https://static.readdy.ai/image/ad65d83c1087407616f0591309583e77/5910930edf8f8435ab6c0a56926fd219.png" 
                      alt="DBD Podcast Logo" 
                      className="h-54 w-auto"
                    />
                  </div>
                  <div className="w-24 h-1 bg-amber-500 mb-6"></div>
                  <h2 className="text-2xl text-amber-400 font-semibold mb-4">
                    Distilled Business Dialogues
                  </h2>
                </div>
                
                <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                  Where business wisdom meets the art of distillation. Join us for deep conversations about entrepreneurship, 
                  leadership, and the psychology behind successful ventures.
                </p>
                
                <div className="flex flex-wrap items-center gap-6 text-amber-400 mb-8">
                  <div className="flex items-center space-x-2">
                    <i className="ri-mic-line text-xl"></i>
                    <span>Monthly Episodes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="ri-user-voice-line text-xl"></i>
                    <span>Expert Guests</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="ri-lightbulb-line text-xl"></i>
                    <span>Business Insights</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/episodes"
                    className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    View or Listen
                  </Link>
                  <a 
                    href="#subscribe"
                    className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold py-3 px-8 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Subscribe Free
                  </a>
                </div>
              </div>

              {/* Right Content - Registration Form */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 max-w-md mx-auto lg:mx-0">
                {user ? (
                  // If user is already logged in
                  <div className="text-center space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <i className="ri-check-circle-line text-green-500 text-3xl mb-3"></i>
                      <h4 className="text-lg font-bold text-green-800 mb-2">Welcome back, {user.user_metadata?.name || user.email}!</h4>
                      <p className="text-green-700">You already have access to all episodes and exclusive content.</p>
                    </div>
                    
                    <Link 
                      to="/episodes"
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 whitespace-nowrap cursor-pointer inline-block text-center"
                    >
                      Browse Episodes
                    </Link>
                  </div>
                ) : (
                  // Registration Form
                  <div>
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">Subscribe to the DBD Podcast</h3>
                      <p className="text-gray-600 text-sm">Register for an exclusive preview of episodes and business insights</p>
                    </div>

                    {step === 'form' && (
                      <form onSubmit={handleSubmitForm} className="space-y-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 text-sm"
                            placeholder="Enter your full name"
                          />
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            id="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 text-sm"
                            placeholder="Enter your email address"
                          />
                        </div>

                        <div>
                          <label htmlFor="country" className="block text-sm font-semibold text-gray-700 mb-2">
                            Country *
                          </label>
                          <div className="relative">
                            <select
                              id="country"
                              value={formData.country}
                              onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                              required
                              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer text-sm"
                            >
                              <option value="">Select your country</option>
                              {countries.map((country) => (
                                <option key={country} value={country}>{country}</option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <i className="ri-arrow-down-s-line text-gray-400"></i>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label htmlFor="industry" className="block text-sm font-semibold text-gray-700 mb-2">
                            Industry
                          </label>
                          <div className="relative">
                            <select
                              id="industry"
                              value={formData.industry}
                              onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer text-sm"
                            >
                              <option value="">Select your industry (optional)</option>
                              {industries.map((industry) => (
                                <option key={industry.id} value={industry.name}>{industry.name}</option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <i className="ri-arrow-down-s-line text-gray-400"></i>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            id="terms"
                            checked={formData.agreeToTerms}
                            onChange={(e) => setFormData(prev => ({ ...prev, agreeToTerms: e.target.checked }))}
                            className="mt-1 h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer"
                          />
                          <label htmlFor="terms" className="text-xs text-gray-600 cursor-pointer">
                            I agree to receive emails about new episodes, exclusive content, and updates. 
                            I understand I can unsubscribe at any time. By subscribing, I agree to the{' '}
                            <Link to="/privacy" className="text-amber-600 hover:text-amber-700 underline">
                              Privacy Policy
                            </Link>.
                          </label>
                        </div>

                        {error && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-center">
                              <i className="ri-error-warning-line text-red-500 text-lg mr-2"></i>
                              <p className="text-red-700 text-xs">{error}</p>
                            </div>
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={isLoading || !formData.name.trim() || !formData.email.trim() || !formData.country || !formData.agreeToTerms}
                          className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold py-3 px-4 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                        >
                          {isLoading ? (
                            <span className="flex items-center justify-center">
                              <i className="ri-loader-4-line animate-spin mr-2"></i>
                              Sending Code...
                            </span>
                          ) : (
                            'Get Free Access'
                          )}
                        </button>

                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-start space-x-2">
                            <i className="ri-gift-line text-amber-500 text-base mt-0.5"></i>
                            <div className="text-xs text-gray-600">
                              <p className="font-medium mb-1">What you'll get:</p>
                              <p>• Early access to new episodes</p>
                              <p>• Exclusive show notes and transcripts</p>
                              <p>• Guest resources and links</p>
                              <p>• Personalized recommendations</p>
                            </div>
                          </div>
                        </div>

                        <p className="text-xs text-gray-500 text-center">
                          Access link will be sent to your email automatically upon registration.
                        </p>
                      </form>
                    )}

                    {step === 'verification' && (
                      <form onSubmit={handleVerifyCode} className="space-y-4">
                        <div className="text-center mb-4">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <i className="ri-mail-check-line text-green-600 text-xl"></i>
                          </div>
                          <h4 className="text-lg font-bold text-gray-800 mb-2">Check Your Email</h4>
                          <p className="text-gray-600 text-sm">
                            We've sent a 6-digit verification code to<br />
                            <strong>{formData.email}</strong>
                          </p>
                        </div>

                        {successMessage && (
                          <div className={`border rounded-lg p-3 ${devCode ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                            <div className="flex items-start">
                              <i className={`text-lg mr-2 mt-0.5 ${devCode ? 'ri-code-line text-blue-500' : 'ri-information-line text-green-500'}`}></i>
                              <div>
                                <p className={`text-xs ${devCode ? 'text-blue-700' : 'text-green-700'}`}>{successMessage}</p>
                                {expiresAt && (
                                  <p className={`text-xs mt-1 ${devCode ? 'text-blue-600' : 'text-green-600'}`}>
                                    Code expires at {formatExpiry(expiresAt)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        <div>
                          <label htmlFor="verification-code" className="block text-sm font-semibold text-gray-700 mb-2">
                            Verification Code
                          </label>
                          <input
                            type="text"
                            id="verification-code"
                            value={verificationCode}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setVerificationCode(value);
                            }}
                            required
                            maxLength={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 text-center text-xl font-mono tracking-widest"
                            placeholder="000000"
                          />
                          <p className="text-xs text-gray-500 mt-2 text-center">
                            Enter the 6-digit code from your email{devCode && ' or use the code shown above'}
                          </p>
                        </div>

                        {error && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-center">
                              <i className="ri-error-warning-line text-red-500 text-lg mr-2"></i>
                              <p className="text-red-700 text-xs">{error}</p>
                            </div>
                          </div>
                        )}

                        <div className="space-y-2">
                          <button
                            type="submit"
                            disabled={isLoading || verificationCode.length !== 6}
                            className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                          >
                            {isLoading ? (
                              <span className="flex items-center justify-center">
                                <i className="ri-loader-4-line animate-spin mr-2"></i>
                                Verifying...
                              </span>
                            ) : (
                              'Complete Registration'
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => setStep('form')}
                            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-arrow-left-line mr-2"></i>
                            Back to Form
                          </button>
                        </div>

                        <div className="text-center">
                          <button
                            type="button"
                            onClick={handleSubmitForm}
                            disabled={isLoading}
                            className="text-amber-600 hover:text-amber-700 text-sm font-medium transition-colors cursor-pointer"
                          >
                            Didn't receive the code? Resend
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Latest Episodes Section - Now with error handling */}
      <section id="episodes" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Latest Episodes</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Dive into our most recent conversations with industry leaders and business experts
            </p>
          </div>

          {episodesLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <i className="ri-loader-4-line animate-spin text-4xl text-amber-500 mb-4"></i>
                <p className="text-gray-600">Loading episodes...</p>
              </div>
            </div>
          ) : episodesError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center max-w-md mx-auto">
              <i className="ri-error-warning-line text-red-500 text-3xl mb-4"></i>
              <h3 className="text-lg font-bold text-red-800 mb-2">Unable to Load Episodes</h3>
              <p className="text-red-700 mb-4">{episodesError}</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 cursor-pointer"
              >
                Retry
              </button>
            </div>
          ) : latestEpisodes.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center max-w-md mx-auto">
              <i className="ri-information-line text-yellow-500 text-3xl mb-4"></i>
              <h3 className="text-lg font-bold text-yellow-800 mb-2">No Episodes Available</h3>
              <p className="text-yellow-700">Check back soon for new episodes!</p>
            </div>
          ) : (
            <>
              <div className={`grid gap-8 max-w-7xl mx-auto mb-12 ${
                latestEpisodes.length === 1 
                  ? 'grid-cols-1 justify-items-center max-w-md' 
                  : latestEpisodes.length === 2 
                  ? 'grid-cols-1 md:grid-cols-2 justify-items-center max-w-4xl' 
                  : 'md:grid-cols-2 lg:grid-cols-3'
              }`}>
                {latestEpisodes.map((episode) => (
                  <div key={episode.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden group">
                    <div 
                      className="h-48 bg-cover bg-center bg-gray-200"
                      style={{
                        backgroundImage: `url('${episode.advertisingImage}')`
                      }}
                    ></div>
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <span>{new Date(episode.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                        <span className="mx-2">•</span>
                        <span>{episode.duration}</span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-amber-600 transition-colors">
                        {episode.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {episode.description}
                      </p>
                      <p className="text-sm text-amber-600 font-medium mb-4">
                        Guest: {episode.guest}
                      </p>
                      <div className="flex items-center justify-between">
                        <Link 
                          to={`/episodes/${episode.id}`}
                          className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-play-circle-line mr-2"></i>
                          Listen
                        </Link>
                        <Link 
                          to={`/episodes/${episode.id}`}
                          className="text-gray-600 hover:text-amber-600 transition-colors cursor-pointer"
                        >
                          Show Notes
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <Link 
                  to="/episodes"
                  className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  View All Episodes
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">About DBD Podcast</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Connecting business wisdom with industry expertise through meaningful conversations
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
              <div 
                className="h-96 rounded-xl bg-cover bg-center shadow-lg"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Business%20professionals%20in%20elegant%20discussion%20setting%20with%20premium%20spirits%20bottles%20and%20glasses%20sophisticated%20networking%20environment%20corporate%20meeting%20atmosphere%20luxury%20business%20lounge%20modern%20office%20interior%20executive%20conversation&width=600&height=400&seq=about-business&orientation=landscape')`
                }}
              ></div>
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-gray-800">Our Mission</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  DBD Podcast explores the intersection of business psychology, entrepreneurship, and industry expertise. 
                  We believe that the best business insights come from deep, thoughtful conversations with leaders who've 
                  navigated complex challenges.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Each episode distills complex business concepts into actionable wisdom, creating a rich dialogue that 
                  you can apply immediately to your own ventures and leadership journey.
                </p>
              </div>
            </div>

            {/* Partner Companies */}
            <div className="bg-gray-50 rounded-2xl p-12">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-gray-800 mb-4">Our Partner Companies</h3>
                <p className="text-lg text-gray-600">
                  DBD Podcast is proudly connected with innovative companies driving change across industries
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="h-32 flex items-center justify-center mb-6 bg-white rounded-lg shadow-sm p-4">
                    <img 
                      src="https://static.readdy.ai/image/ad65d83c1087407616f0591309583e77/9c25d64c5ac3ab297f35ee4da9d294ac.png"
                      alt="AliasSmith Logo"
                      className="max-h-20 max-w-full object-contain"
                    />
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">AliasSmith</h4>
                  <p className="text-gray-600 mb-4">
                    Connecting exceptional spirits to global markets with innovative-, sustainable-, digital-, and expert brand management.
                  </p>
                  <a 
                    href="https://aliassmith.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-amber-600 hover:text-amber-700 font-medium cursor-pointer"
                  >
                    Visit AliasSmith →
                  </a>
                </div>

                <div className="text-center">
                  <div className="h-32 flex items-center justify-center mb-6 bg-white rounded-lg shadow-sm p-4">
                    <img 
                      src="https://static.readdy.ai/image/ad65d83c1087407616f0591309583e77/72d25a49c677ce93c2bd4efb801b7e2e.png"
                      alt="Jumpify Logo"
                      className="max-h-20 max-w-full object-contain"
                    />
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">Jumpify.net</h4>
                  <p className="text-gray-600 mb-4">
                    Simplifies logistics for beverage brands with marketing, 3PL, admin, and distribution support across Europe.
                  </p>
                  <a 
                    href="https://jumpify.net" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-amber-600 hover:text-amber-700 font-medium cursor-pointer"
                  >
                    Visit Jumpify →
                  </a>
                </div>

                <div className="text-center">
                  <div className="h-32 flex items-center justify-center mb-6 bg-white rounded-lg shadow-sm p-4">
                    <img 
                      src="https://static.readdy.ai/image/ad65d83c1087407616f0591309583e77/b3caad7867a92c81bbee2129bba05024.png"
                      alt="Los Chapulines Logo"
                      className="max-h-20 max-w-full object-contain"
                    />
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">Los Chapulines</h4>
                  <p className="text-gray-600 mb-4">
                    On-Trade team in top EU cities promotes select spirits, boosting brand awareness and sales in hospitality.
                  </p>
                  <a 
                    href="https://loschapulines.org" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-amber-600 hover:text-amber-700 font-medium cursor-pointer"
                  >
                    Visit Los Chapulines →
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section id="subscribe" className="py-20 bg-gradient-to-r from-gray-900 to-gray-800">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Never Miss an Episode</h2>
            <p className="text-xl text-gray-300 mb-12">
              Subscribe to get the latest episodes and exclusive business insights delivered to your inbox
            </p>
            
            <div className="flex justify-center space-x-6 mb-12">
              <a href="#" className="text-gray-400 hover:text-amber-400 transition-colors cursor-pointer">
                <i className="ri-spotify-fill text-4xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-amber-400 transition-colors cursor-pointer">
                <i className="ri-apple-fill text-4xl"></i>
              </a>
              <a href="#" className="text-gray-4 hover:text-amber-400 transition-colors cursor-pointer">
                <i className="ri-google-fill text-4xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-amber-400 transition-colors cursor-pointer">
                <i className="ri-youtube-fill text-4xl"></i>
              </a>
            </div>

            {user ? (
              <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-6">
                <i className="ri-check-circle-line text-green-400 text-3xl mb-3"></i>
                <p className="text-green-300 text-lg">You're already subscribed! Thanks for being part of the DBD community.</p>
              </div>
            ) : (
              <button 
                onClick={() => setIsRegistrationModalOpen(true)}
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 px-12 rounded-lg transition-all duration-200 transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Subscribe Now
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      <RegistrationModal 
        isOpen={isRegistrationModalOpen}
        onClose={() => setIsRegistrationModalOpen(false)}
        onRegistrationSuccess={handleRegistrationSuccess}
      />
    </div>
  );
}